import { useCallback, useEffect, useState } from "react";
import { Bot, Check, EyeOff, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { adminApi, errText } from "./adminApi";

const fmtTs = (ts) => new Date(ts * 1000).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });

export default function AdminAutoQuestions() {
  const [overview, setOverview] = useState(null);
  const [topics, setTopics] = useState([]);
  const [review, setReview] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [genTopic, setGenTopic] = useState("");
  const [genCount, setGenCount] = useState(5);
  const [genDifficulty, setGenDifficulty] = useState("Mixed");
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);

  const load = useCallback(async () => {
    const [{ data: o }, { data: t }, { data: r }] = await Promise.all([
      adminApi.get("/admin/ai/overview"), adminApi.get("/admin/test/topics"), adminApi.get("/admin/ai/review")]);
    setOverview(o);
    setTopics(t.topics);
    setReview(r.questions);
  }, []);
  useEffect(() => { load().catch(() => setError("Could not load AI settings. Click Refresh.")); }, [load]);

  const saveSettings = async (patch) => {
    setError("");
    try {
      const { data } = await adminApi.put("/admin/ai/settings", patch);
      setOverview((o) => ({ ...o, settings: data.settings }));
      setNotice("AI settings saved.");
    } catch (err) { setError(errText(err)); }
  };

  const generate = async () => {
    if (!genTopic) { setError("Pick a topic to generate questions for."); return; }
    setGenerating(true);
    setError("");
    setGenResult(null);
    try {
      const { data } = await adminApi.post("/admin/ai/generate", { topic_id: genTopic, count: genCount, difficulty: genDifficulty });
      setGenResult(data);
      await load();
    } catch (err) { setError(errText(err)); }
    setGenerating(false);
  };

  const reviewAction = async (qid, action) => {
    try {
      await adminApi.post(`/admin/ai/review/${qid}`, { action });
      setNotice(`Question ${action}d.`);
      await load();
    } catch (err) { setError(errText(err)); }
  };

  const s = overview?.settings;
  const stats = overview?.stats;

  return (
    <div data-testid="admin-ai-studio">
      {notice && <p className="tp-success-box" style={{ background: "#123a2e", border: "1px solid #12b76a", color: "#4ade80" }}>{notice}</p>}
      {error && <p className="admin-error" data-testid="ai-error">{error}</p>}

      {stats && <div className="stat-cards" data-testid="ai-stats">
        {[["Curated bank", stats.curated], ["NEET PYQs", stats.pyq], ["AI generated", stats.ai], ["AI active", stats.ai_active], ["Awaiting review", stats.unverified + stats.flagged]].map(([label, v]) =>
          <div className="stat-card" key={label}><b>{v}</b><span>{label.toUpperCase()}</span></div>)}
      </div>}

      {s && <div className="admin-card" data-testid="ai-settings">
        <div className="admin-row" style={{ justifyContent: "space-between" }}>
          <h4><Bot size={15} style={{ verticalAlign: -2 }} /> Automatic question engine</h4>
          <button className="admin-btn" onClick={() => load()} data-testid="ai-refresh"><RefreshCw size={14} /></button>
        </div>
        <div className="admin-row" style={{ marginTop: 10 }}>
          <label className="admin-label" style={{ flex: 1, minWidth: 200 }}>GENERATING MODEL
            <select className="admin-input" value={s.provider} onChange={(e) => saveSettings({ provider: e.target.value })} data-testid="ai-provider">
              {(overview.providers || []).map((p) => <option key={p.key} value={p.key}>{({ openai: "ChatGPT / OpenAI", gemini: `Gemini (${p.model})`, anthropic: `Claude (${p.model})` })[p.key] || p.key}</option>)}
            </select>
          </label>
          {s.provider === "openai" && (<>
            <label className="admin-label" style={{ flex: 1, minWidth: 180 }}>DRAFT MODEL — writes questions
              <select className="admin-input" value={s.draft_model || "gpt-6-luna"} onChange={(e) => saveSettings({ draft_model: e.target.value })} data-testid="ai-draft-model">
                {(overview.openai_models || []).map((m) => <option key={m} value={m}>{m}{m === "gpt-6-luna" ? " (cheapest)" : ""}</option>)}
              </select>
            </label>
            <label className="admin-label" style={{ flex: 1, minWidth: 180 }}>VERIFIER MODEL — checks answers
              <select className="admin-input" value={s.verify_model || "gpt-6.1-sol"} onChange={(e) => saveSettings({ verify_model: e.target.value })} data-testid="ai-verify-model">
                {(overview.openai_models || []).map((m) => <option key={m} value={m}>{m}{m === "gpt-6-astra" ? " (best quality)" : ""}</option>)}
              </select>
            </label>
          </>)}
          <label className="admin-label" style={{ flex: 1, minWidth: 200 }}>HOMEWORK GRADING — who checks written work
            <select className="admin-input" value={s.grading_provider || ""} onChange={(e) => saveSettings({ grading_provider: e.target.value || null })} data-testid="ai-grading-provider">
              <option value="">Same as generating model</option>
              {(overview.providers || []).map((p) => <option key={p.key} value={p.key}>{({ openai: "ChatGPT / OpenAI", gemini: `Gemini`, anthropic: `Claude (better feedback)` })[p.key] || p.key}</option>)}
            </select>
          </label>
          <label className="admin-label" style={{ flex: 1, minWidth: 180 }}>DEFAULT DIFFICULTY
            <select className="admin-input" value={s.default_difficulty} onChange={(e) => saveSettings({ default_difficulty: e.target.value })} data-testid="ai-difficulty">
              {["Mixed", "Easy", "Medium", "Hard"].map((d) => <option key={d}>{d}</option>)}
            </select>
          </label>
        </div>
        <label className="check-row" style={{ marginTop: 8 }}>
          <input type="checkbox" checked={!!s.ai_enabled} onChange={(e) => saveSettings({ ai_enabled: e.target.checked })} data-testid="ai-live-toggle" />
          <span><b>Live AI generation</b> — quizzes &amp; the Daily Question create fresh questions in real time (the cheap draft model writes them, the verifier model checks each answer in the background; disagreements are auto-hidden and land in the review queue below). If generation ever fails, quizzes fall back to the bank automatically.</span>
        </label>
        <label className="check-row">
          <input type="checkbox" checked={!!s.pyq_enabled} onChange={(e) => saveSettings({ pyq_enabled: e.target.checked })} data-testid="ai-pyq-toggle" />
          <span><b>Include NEET PYQs</b> — verified previous-year questions stay in the quiz/challenge/daily pool.</span>
        </label>
      </div>}

      <div className="admin-card" data-testid="ai-generate">
        <h4><Sparkles size={15} style={{ verticalAlign: -2 }} /> Generate questions now</h4>
        <p className="lead-sub">Batch-generate questions straight into the bank. The cheap draft model writes them; the verifier model checks each answer before going live — mismatches are auto-hidden and flagged for review.</p>
        <div className="admin-row" style={{ marginTop: 8 }}>
          <select className="admin-input" style={{ flex: 1, minWidth: 200 }} value={genTopic} onChange={(e) => setGenTopic(e.target.value)} data-testid="ai-gen-topic">
            <option value="">Select topic…</option>
            {topics.map((t) => <option key={t.topic_id} value={t.topic_id}>{t.name}</option>)}
          </select>
          <select className="admin-input" style={{ width: 100 }} value={genCount} onChange={(e) => setGenCount(Number(e.target.value))} data-testid="ai-gen-count">
            {[1, 2, 3, 5, 8, 10].map((n) => <option key={n} value={n}>{n} Qs</option>)}
          </select>
          <select className="admin-input" style={{ width: 120 }} value={genDifficulty} onChange={(e) => setGenDifficulty(e.target.value)} data-testid="ai-gen-difficulty">
            {["Mixed", "Easy", "Medium", "Hard"].map((d) => <option key={d}>{d}</option>)}
          </select>
          <button className="admin-btn" disabled={generating || !genTopic} onClick={generate} data-testid="ai-generate-btn">
            {generating ? "Generating… (may take ~30s)" : "Generate"}</button>
        </div>
        {genResult && <div style={{ marginTop: 12 }} data-testid="ai-gen-result">
          <p className="lead-sub" style={{ color: "#4ade80" }}>{genResult.created.length} questions added to {genResult.topic_name} via {genResult.provider} · {genResult.verified_ok} passed cross-verification.</p>
          {genResult.created.map((q, i) => <div key={q.question_id} className="admin-row" style={{ marginTop: 6 }} data-testid={`ai-gen-row-${i}`}>
            <span className={`status-pill ${q.verification === "agreed" ? "approved" : q.verification === "disagreed" ? "failed" : "review"}`}>{q.verification === "agreed" ? "VERIFIED" : q.verification === "disagreed" ? "FLAGGED" : "UNCHECKED"}</span>
            <span style={{ flex: 1, color: "#dbe6f4", fontSize: 12 }}>{q.text.slice(0, 130)}{q.text.length > 130 ? "…" : ""}</span>
            <span className="lead-sub">Ans: {q.correct}</span>
          </div>)}
        </div>}
      </div>

      <div className="admin-card" data-testid="ai-review">
        <h4>Review queue ({review.length})</h4>
        <p className="lead-sub">AI questions that failed cross-verification or are awaiting it. Approve to keep them live, hide to shelve, or delete.</p>
        {review.length === 0 && <div className="admin-empty" style={{ marginTop: 8 }}>Nothing pending — every AI question is verified.</div>}
        {review.map((q, i) => <div key={q.question_id} style={{ marginTop: 10, borderTop: "1px solid #22314a", paddingTop: 10 }} data-testid={`ai-review-row-${i}`}>
          <div className="admin-row">
            <span className={`status-pill ${q.flagged ? "failed" : "review"}`}>{q.flagged ? "FLAGGED" : "PENDING"}</span>
            <span className="status-pill locked">{q.topic_name}</span>
            <span style={{ flex: 1, color: "#dbe6f4", fontSize: 12 }}>{q.text.slice(0, 120)}{q.text.length > 120 ? "…" : ""}</span>
          </div>
          {q.flag_reason && <p className="lead-sub" style={{ color: "#ff9e9e", marginTop: 4 }}>{q.flag_reason}</p>}
          <p className="lead-sub" style={{ marginTop: 4 }}>Marked answer: {q.correct} · {q.options?.[q.correct]}</p>
          <div className="admin-row" style={{ marginTop: 6 }}>
            <button className="admin-btn" onClick={() => reviewAction(q.question_id, "approve")} data-testid={`ai-approve-${i}`}><Check size={13} /> Approve</button>
            <button className="admin-btn" onClick={() => reviewAction(q.question_id, "disable")} data-testid={`ai-disable-${i}`}><EyeOff size={13} /> Hide</button>
            <button className="admin-btn" onClick={() => reviewAction(q.question_id, "delete")} data-testid={`ai-delete-${i}`}><Trash2 size={13} /></button>
          </div>
        </div>)}
      </div>

      {overview && <div className="admin-card" data-testid="ai-logs">
        <h4>Recent generation activity</h4>
        {overview.logs.length === 0 && <div className="admin-empty" style={{ marginTop: 8 }}>No generations yet.</div>}
        {overview.logs.map((log, i) => <div className="admin-row" key={i} style={{ marginTop: 6 }} data-testid={`ai-log-${i}`}>
          <span className={`status-pill ${log.status === "ok" ? "approved" : "failed"}`}>{log.status === "ok" ? `${log.count} Qs` : "FAILED"}</span>
          <span className="lead-sub" style={{ flex: 1 }}>{log.topic_name} · {log.trigger} · {log.provider} · {fmtTs(log.ts)}{log.latency_ms ? ` · ${(log.latency_ms / 1000).toFixed(1)}s` : ""}{log.error ? ` — ${log.error.slice(0, 80)}` : ""}</span>
        </div>)}
      </div>}
    </div>
  );
}
